// One copy source for public FAQ disclosures and their schema.org projection.
// The page components render these returned arrays verbatim; FAQPage is then derived from the
// same array so structured data cannot accumulate questions or claims that are absent on screen.

const textOf = (value, fallback = "") => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return fallback;
  return String(
    value.description || value.detail || value.summary || value.copy || value.body || fallback,
  ).trim();
};

const titleOf = (value, fallback = "") => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object") return fallback;
  return String(value.title || value.label || value.name || fallback).trim();
};

const asSentence = (value) => {
  const text = textOf(value);
  if (!text) return "";
  return /[.!?]$/.test(text) ? text : `${text}.`;
};

/** Build the four questions rendered on every canonical /modules/:key page. */
export function moduleFaqsFor({
  mod,
  narrative,
  related = [],
  featureMeta = null,
  tierName = null,
  evaluationNote = "",
}) {
  if (!mod || !narrative) return [];
  const relatedNames = related.map((item) => item?.name).filter(Boolean).join(", ");
  const prerequisites = narrative.beforeRollout?.prerequisites || [];
  const preparation = [featureMeta?.requirement, ...prerequisites, evaluationNote]
    .map(asSentence)
    .filter(Boolean)
    .join(" ");
  const planAnswer = tierName
    ? `${mod.name} is included from ${tierName} in the current public plan matrix. Final rollout scope, services and external dependencies are confirmed in your written proposal.`
    : "Plan inclusion and rollout scope depend on the live plan matrix, readiness state and your school's written proposal. The demo team will confirm the applicable boundary before rollout.";
  const aiAnswer = narrative.aiSupport
    ? narrative.aiSupport.mode === "deterministic"
      ? `${narrative.aiSupport.label}. The documented signal is computed from configured rules and recorded data; it is not a model decision.`
      : `${narrative.aiSupport.label}. Aira supports only the reviewed actions listed on this page, and authorised people remain responsible for confirming material actions.`
    : `No AI capability is asserted for ${mod.name}. Its documented work remains controlled by school records, roles and permissions.`;

  return [
    {
      q: `Does ${mod.name} work on its own?`,
      a: `${relatedNames} are the documented neighbouring capabilities. They help a school evaluate the surrounding hand-offs, but a related page does not mean every integration is automatic or included in every plan.`,
    },
    { q: `What should our school prepare before enabling ${mod.name}?`, a: preparation },
    { q: `Is AI used in ${mod.name}?`, a: aiAnswer },
    { q: "How do we confirm the plan and implementation scope?", a: planAnswer },
  ];
}
/** Normalise only the buyer-guide FAQ records that the page actually renders. */
export function buyerGuideFaqsFor(faqs = []) {
  return (Array.isArray(faqs) ? faqs : []).map((faq, index) => ({
    q: String(faq?.q || faq?.question || titleOf(faq, `Question ${index + 1}`)).trim(),
    a: String(faq?.a || faq?.answer || textOf(faq)).trim(),
  })).filter(({ q, a }) => q && a);
}

/** Project a rendered FAQ array into one valid FAQPage graph node, or no node at all. */
export function faqPageSchemaFor(faqs = []) {
  const entries = buyerGuideFaqsFor(faqs);
  if (!entries.length) return null;
  return {
    "@type": "FAQPage",
    mainEntity: entries.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: {
        "@type": "Answer",
        text: a,
      },
    })),
  };
}
